import type {
  LintFinding,
  MakerAgent,
  MakerAgentUpsert,
  MarketplaceSettings,
  OwnerRef,
  OwnerView,
  PublishRequest,
  PublishResponse,
  VersionRestoreResult
} from '@ralph/contracts'
import { api, apiGet, apiSend } from './client.ts'

/**
 * The maker and administrator halves of the marketplace API.
 *
 * Split from `api/marketplace.ts` (the consumer gallery) on purpose: these calls are the
 * only ones that can change what an agent is, and keeping them in their own module makes
 * "which requests can write an agent" a question answerable by reading one file.
 *
 * Every request body here is a contract type. The maker plane's whole safety property is
 * that a maker can only send `MakerAgentUpsert` — a strict schema with no model, no
 * numbers, no approval knobs (AGM-021) — so this client never assembles a body by hand.
 *
 * // WIRE: B2 owns `apps/api/src/routes/maker.ts` + `apps/api/src/routes/admin/marketplace.ts`.
 * // The response envelopes this module expects are stated per call below; where the memo's
 * // route map left the envelope open, the shape here is the contract to implement against.
 */

// ---------------------------------------------------------------------------
// Types the contracts package does not carry (transport envelopes and the two
// catalogue reads the builder needs).
// ---------------------------------------------------------------------------

/**
 * One entry in "What it can do".
 *
 * Builtin abilities and connected capabilities arrive already merged and already
 * translated: `id` is the grant identifier the server stores, and it is the ONLY technical
 * string in the payload — it is never rendered. `wording` carries the read/act distinction
 * (AGM-080) as an enum so the copy for it lives in the client's string module rather than
 * in a database row that might arrive un-curated.
 *
 * // WIRE: B2 must implement `GET /api/maker/capabilities` → `{ items: MakerCapability[] }`,
 * // sourced from admin-approved, enabled, non-quarantined catalogue entries with curated
 * // copy (uncurated entries are hidden from makers entirely). `system` groups the list in
 * // the picker; send the curated system label, or omit it for platform builtins.
 */
export type MakerCapability = {
  id: string
  name: string
  description: string
  wording: 'reads' | 'acts'
  system?: string
}

/** A save answers with the stored agent AND the lint findings that ran over it (AGM-024). */
export type MakerSaveResult = { agent: MakerAgent; lint: LintFinding[] }

export type MakerVersionSummary = {
  version: number
  changeNote: string
  changedByName: string
  createdAt: string
}

/**
 * What a publication did (PRD-26 D-40): what could go live went live (`published`), and what
 * needs an administrator became one sharing request (`request`). Either may be absent.
 */
export type PublishOutcome = PublishResponse

export type EditorInviteOutcome = { status: 'accepted' | 'declined' }

/** Admin curation rows: every listing on the platform, not just the ones shared with the admin. */
export type AdminListingRow = {
  agentId: string
  displayName: string
  ownerName: string
  state: MakerAgent['state']
  category: string
  official: boolean
  featuredRank: number | null
  audienceKind: 'private' | 'groups' | 'everyone'
  health: 'ok' | 'attention'
  updatedAt: string
}

/**
 * Something nobody can maintain any more (AGM-025, PRD-26 GRP-030): an agent or a knowledge base
 * whose personal owner has left, or whose owning group has no active manager.
 */
export type NeedsOwnerRow = {
  kind: 'agent' | 'knowledge_base'
  id: string
  name: string
  owner: OwnerView
  since: string
}

// ---------------------------------------------------------------------------
// Maker plane
// ---------------------------------------------------------------------------

const agentPath = (agentId: string): string => `/api/maker/agents/${encodeURIComponent(agentId)}`

/** Own agents plus the ones the caller co-edits — including invitations not yet accepted. */
export const fetchMakerAgents = (): Promise<{ items: MakerAgent[] }> =>
  apiGet<{ items: MakerAgent[] }>('/api/maker/agents')

export const fetchMakerAgent = (agentId: string): Promise<MakerAgent> => apiGet<MakerAgent>(agentPath(agentId))

/** `owner` omitted is the creator; a group needs its manager or an administrator (PRD-26 D-19). */
export const createMakerAgent = (input: {
  agentId: string
  displayName: string
  owner?: OwnerRef
}): Promise<MakerAgent> => apiSend<MakerAgent>('/api/maker/agents', 'POST', input)

/**
 * Saves the essentials.
 *
 * `If-Match` carries the definition version the edit was based on, exactly as the admin
 * studio does — the server answers 412 (or 409 through a proxy) when someone else saved
 * first, which the builder renders as the "someone saved while you were editing" state
 * rather than as an error code (AGM-027).
 */
export const saveMakerAgent = (
  agentId: string,
  definitionVersion: number,
  input: MakerAgentUpsert
): Promise<MakerSaveResult> =>
  api<MakerSaveResult>(agentPath(agentId), {
    method: 'PUT',
    headers: { 'if-match': String(definitionVersion) },
    body: JSON.stringify(input)
  })

/**
 * Publishes for the audience chosen. What the maker may make live alone goes live now; groups
 * they do not manage, or everyone, go to an administrator as one sharing request (D-13, D-40).
 */
export const publishMakerAgent = (agentId: string, input: PublishRequest): Promise<PublishOutcome> =>
  apiSend<PublishOutcome>(`${agentPath(agentId)}/publish`, 'POST', input)

export const unpublishMakerAgent = (agentId: string): Promise<MakerAgent> =>
  apiSend<MakerAgent>(`${agentPath(agentId)}/unpublish`, 'POST')

export const archiveMakerAgent = (agentId: string): Promise<MakerAgent> =>
  apiSend<MakerAgent>(`${agentPath(agentId)}/archive`, 'POST')

/**
 * Brings a retired agent back for the audience chosen, by the same rule as publishing: what needs
 * an administrator waits, and the agent stays retired until nothing of it can go live (D-50).
 */
export const restoreMakerAgent = (agentId: string, input: PublishRequest): Promise<PublishOutcome> =>
  apiSend<PublishOutcome>(`${agentPath(agentId)}/restore`, 'POST', input)

/**
 * Moves an agent to another owner (D-32): its maintainers move it to a group they manage;
 * administrators move it anywhere. Who can use it never changes (D-9).
 */
export const moveMakerAgent = (agentId: string, to: OwnerRef): Promise<MakerAgent> =>
  apiSend<MakerAgent>(`${agentPath(agentId)}/move`, 'POST', { to })

/**
 * Opens a preview conversation against the DRAFT head.
 *
 * Returns a real conversation id: the preview chat then runs through the same message
 * endpoint and the same SSE stream as production, which is what makes the preview
 * trustworthy (AGM-029). The server marks it `preview` so it stays out of the caller's
 * conversation list and out of consumer analytics.
 */
export const startMakerPreview = (agentId: string): Promise<{ conversationId: string }> =>
  apiSend<{ conversationId: string }>(`${agentPath(agentId)}/preview`, 'POST')

export const fetchMakerVersions = (agentId: string): Promise<{ items: MakerVersionSummary[] }> =>
  apiGet<{ items: MakerVersionSummary[] }>(`${agentPath(agentId)}/versions`)

/** Restoring re-validates attachments against current policy, so it can come back short (AGM-104). */
export const restoreMakerVersion = (agentId: string, version: number): Promise<VersionRestoreResult> =>
  apiSend<VersionRestoreResult>(`${agentPath(agentId)}/versions/${version}/restore`, 'POST')

export const inviteMakerEditor = (agentId: string, email: string): Promise<MakerAgent> =>
  apiSend<MakerAgent>(`${agentPath(agentId)}/editors`, 'POST', { email })

export const removeMakerEditor = (agentId: string, userId: string): Promise<MakerAgent> =>
  apiSend<MakerAgent>(`${agentPath(agentId)}/editors/${encodeURIComponent(userId)}`, 'DELETE')

/** Co-editorship requires the invitee's own acceptance — nobody is conscripted (AGM-026). */
export const respondToEditorInvite = (agentId: string, decision: 'accept' | 'decline'): Promise<EditorInviteOutcome> =>
  apiSend<EditorInviteOutcome>(`/api/maker/editor-invites/${encodeURIComponent(agentId)}/${decision}`, 'POST')

export const fetchMakerCapabilities = (): Promise<{ items: MakerCapability[] }> =>
  apiGet<{ items: MakerCapability[] }>('/api/maker/capabilities')

/**
 * The curated category taxonomy, as the maker plane sees it.
 *
 * // WIRE: B2 must implement `GET /api/maker/categories` → `{ items: string[] }`, returning
 * // `settings.categories` verbatim (AGM-006: categories are admin-curated, and makers pick
 * // from the list rather than typing one). The admin settings route cannot serve this — it
 * // is admin-gated, and every authenticated maker needs the list. The builder falls back to
 * // a free-text field when this 404s.
 */
export const fetchMakerCategories = (): Promise<{ items: string[] }> =>
  apiGet<{ items: string[] }>('/api/maker/categories')

// ---------------------------------------------------------------------------
// Administrator plane
// ---------------------------------------------------------------------------

const adminAgentPath = (agentId: string): string => `/api/admin/marketplace/agents/${encodeURIComponent(agentId)}`

export const suspendListing = (
  agentId: string,
  input: { reasonPublic: string; reasonFull: string; replacementAgentId: string | null }
): Promise<{ state: MakerAgent['state'] }> =>
  apiSend<{ state: MakerAgent['state'] }>(`${adminAgentPath(agentId)}/suspend`, 'POST', input)

export const unsuspendListing = (agentId: string): Promise<{ state: MakerAgent['state'] }> =>
  apiSend<{ state: MakerAgent['state'] }>(`${adminAgentPath(agentId)}/unsuspend`, 'POST')

/** `rank: null` unpins. Featured order is admin-curated and never popularity (AGM-006). */
export const featureListing = (agentId: string, rank: number | null): Promise<{ featuredRank: number | null }> =>
  apiSend<{ featuredRank: number | null }>(`${adminAgentPath(agentId)}/feature`, 'POST', { rank })

export const setListingOfficial = (agentId: string, value: boolean): Promise<{ official: boolean }> =>
  apiSend<{ official: boolean }>(`${adminAgentPath(agentId)}/official`, 'POST', { value })

export const fetchNeedsOwner = (): Promise<{ items: NeedsOwnerRow[] }> =>
  apiGet<{ items: NeedsOwnerRow[] }>('/api/admin/marketplace/needs-owner')

/**
 * Every listing on the platform, for curation.
 *
 * // WIRE: B2 must implement `GET /api/admin/marketplace/listings` → `{ items: AdminListingRow[] }`
 * // (admin/operator read). The consumer gallery cannot stand in for it: the gallery is
 * // per-viewer by construction (AGM-002), so an admin curating from it would only ever see
 * // the listings already shared with them. The Curation tab degrades to an empty state if
 * // this 404s rather than blocking the rest of the studio.
 */
export const fetchAdminListings = (): Promise<{ items: AdminListingRow[] }> =>
  apiGet<{ items: AdminListingRow[] }>('/api/admin/marketplace/listings')

export const fetchMarketplaceSettings = (): Promise<MarketplaceSettings> =>
  apiGet<MarketplaceSettings>('/api/admin/marketplace/settings')

export const saveMarketplaceSettings = (settings: MarketplaceSettings): Promise<MarketplaceSettings> =>
  api<MarketplaceSettings>('/api/admin/marketplace/settings', { method: 'PUT', body: JSON.stringify(settings) })
